import { useMemo, useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon, Divider, Text } from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import { formatNumber } from '~/utils/common';

import type { CollectingResultReportSummary } from '~/types/delivery-agency.types';

import styles from './CollectingResultReportSummary.module.scss';

const SUMMARY_ITEMS = [
  { label: '전체 운송장', className: 'total', textColor: 'kurly', valueKey: 'totalCount' },
  {
    label: '진행 중',
    className: 'in-progress',
    textColor: 'info',
    valueKey: 'IN_PROGRESS',
    sub: [
      { label: '회수 예정', valueKey: 'COLLECT_ACCEPTED' },
      { label: '회수 출발 준비', valueKey: 'COLLECT_DEPARTURE_READY' },
      { label: '회수 중', valueKey: 'COLLECT_ON_THE_WAY' },
    ],
  },
  { label: '회수 완료', className: 'completed', textColor: 'success', valueKey: 'COLLECT_COMPLETED' },
  { label: '회수 실패', className: 'failed', textColor: 'warning', valueKey: 'COLLECT_FAILED' },
  { label: '회수 취소', className: 'canceled', textColor: 'tertiary', valueKey: 'COLLECT_CANCELED' },
] as const;

type TypeSummaryItem = (typeof SUMMARY_ITEMS)[number];

export type TypeCollectingResultReportSummaryValueKey = TypeSummaryItem['valueKey'];

export type TypeCollectingResultReportSummarySubValueKey = Extract<
  TypeSummaryItem,
  { sub: readonly unknown[] }
>['sub'][number]['valueKey'];

/**
 * `진행 중`은 서버가 내려주는 상태가 아니라 `회수 예정 + 회수 출발 준비 + 회수 중` 합산값이다.
 */
export const IN_PROGRESS_STATUSES: TypeCollectingResultReportSummarySubValueKey[] = [
  'COLLECT_ACCEPTED',
  'COLLECT_DEPARTURE_READY',
  'COLLECT_ON_THE_WAY',
];

const buildCountByValueKey = (summary: CollectingResultReportSummary | null): Record<string, number> => ({
  totalCount: summary?.totalCount ?? 0,
  IN_PROGRESS:
    (summary?.collectAcceptedCount ?? 0) +
    (summary?.collectDepartureReadyCount ?? 0) +
    (summary?.collectOnTheWayCount ?? 0),
  COLLECT_ACCEPTED: summary?.collectAcceptedCount ?? 0,
  COLLECT_DEPARTURE_READY: summary?.collectDepartureReadyCount ?? 0,
  COLLECT_ON_THE_WAY: summary?.collectOnTheWayCount ?? 0,
  COLLECT_COMPLETED: summary?.collectCompletedCount ?? 0,
  COLLECT_FAILED: summary?.collectFailedCount ?? 0,
  COLLECT_CANCELED: summary?.collectCanceledCount ?? 0,
});

/**
 * `진행 중` 카드는 우측 화살표로 하위 상태(회수 예정·회수 출발 준비·회수 중) 건수를 펼쳐 보인다 (조회 전용).
 */
const CollectingResultReportSummaryBoard = ({
  summary,
  selectedValueKeys,
  onSelect,
}: {
  summary: CollectingResultReportSummary | null;
  selectedValueKeys: TypeCollectingResultReportSummaryValueKey[];
  onSelect: (valueKey: TypeCollectingResultReportSummaryValueKey) => void;
}) => {
  const data = useMemo(() => buildCountByValueKey(summary), [summary]);

  const [isInProgressOpen, setIsInProgressOpen] = useState(false);

  return (
    <div className={styles.container}>
      {SUMMARY_ITEMS.map((item) => {
        if ('sub' in item) {
          return (
            <div
              key={item.valueKey}
              className={clsx(styles.box, styles[item.className], {
                [styles.active]: selectedValueKeys.includes(item.valueKey),
              })}
            >
              <button type="button" className={styles['in-progress-filter']} onClick={() => onSelect(item.valueKey)}>
                <Text size="large" weight="semi-bold" color={item.textColor}>
                  {item.label}
                </Text>
                <Text size="xxlarge" weight="semi-bold" color={item.textColor}>
                  {formatNumber(data[item.valueKey] ?? 0)}건
                </Text>
              </button>
              <Divider color="gray200" orientation="vertical" className={styles['in-progress-divider']} />
              <button
                type="button"
                className={styles['in-progress-toggle']}
                aria-label={isInProgressOpen ? '진행 중 상세 접기' : '진행 중 상세 펼치기'}
                aria-expanded={isInProgressOpen}
                onClick={() => setIsInProgressOpen((prev) => !prev)}
              >
                {isInProgressOpen ? (
                  <ChevronUpIcon size={20} color="#0566CD" />
                ) : (
                  <ChevronDownIcon size={20} color="#0566CD" />
                )}
              </button>
              {isInProgressOpen && (
                <div className={styles['in-progress-body']}>
                  {item.sub.map((subItem) => (
                    <div key={subItem.valueKey} className={styles['breakdown-row']}>
                      <span className={styles['breakdown-label']}>
                        <span className={styles['breakdown-dot']} />
                        <Text size="small" color="secondary" element="span">
                          {subItem.label}
                        </Text>
                      </span>
                      <Text size="small" weight="semi-bold" color="info" element="span">
                        {formatNumber(data[subItem.valueKey] ?? 0)}건
                      </Text>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }

        return (
          <button
            key={item.valueKey}
            type="button"
            className={clsx(styles.box, styles[item.className], {
              [styles.active]: selectedValueKeys.includes(item.valueKey),
            })}
            onClick={() => onSelect(item.valueKey)}
          >
            <div className={styles.main}>
              <Text size="large" weight="semi-bold" color={item.textColor}>
                {item.label}
              </Text>
              <Text size="xxlarge" weight="semi-bold" color={item.textColor}>
                {formatNumber(data[item.valueKey] ?? 0)}건
              </Text>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default CollectingResultReportSummaryBoard;
